/**
 * inbox-conditions.ts — Bộ lọc nâng cao của danh sách hội thoại (kiểu SaleSmartly).
 *
 * Thuần dữ liệu: định nghĩa trường, toán tử, nhãn tiếng Việt, kiểm tra một
 * điều kiện đã đủ để gửi chưa, và chuỗi hoá gửi lên backend (`?conditions=`).
 * KHỚP backend `ecdp_backend_source/src/modules/chat/inbox-conditions.ts`.
 */

import { i18n } from '@/lib/i18n'

export type ConditionField =
  | 'tag'
  | 'account'
  | 'channel'
  | 'email'
  | 'phone'
  | 'assignee'
  | 'lastMessageAt'
  | 'lastCustomerMessageAt'
  | 'createdAt'
  | 'threadType'
  | 'status'
  | 'aiMode'
  | 'lifecycle'

export type ConditionLogic = 'and' | 'or'

export interface RelativeValue {
  amount: number
  unit: 'hour' | 'day'
}

export interface InboxCondition {
  field: ConditionField
  op: string
  value?: unknown
}

/** Giá trị đặc biệt của "Nhân viên tiếp đón": chính người đang xem. */
export const ME = '__me__'

/** Kiểu ô nhập giá trị theo trường (quyết định bộ chọn trong giao diện). */
export type ValueKind = 'tags' | 'accounts' | 'channels' | 'members' | 'text' | 'time' | 'single' | 'multi'

export interface FieldDef {
  label: string
  kind: ValueKind
  ops: { value: string; label: string }[]
  /** Lựa chọn cố định cho kind = single | multi. */
  options?: { value: string; label: string }[]
}

/** Lựa chọn có nhãn đọc i18n lúc dùng (getter) → luôn theo ngôn ngữ đang chọn. */
const opt = (value: string, label: () => string) => ({ value, get label() { return label() } })

const LIST_OPS = [
  opt('in', () => i18n.t('convList:cond.op.in')),
  opt('not_in', () => i18n.t('convList:cond.op.notIn')),
]
const TEXT_OPS = [
  opt('contains', () => i18n.t('convList:cond.op.contains')),
  opt('not_contains', () => i18n.t('convList:cond.op.notContains')),
  opt('empty', () => i18n.t('convList:cond.op.empty')),
  opt('not_empty', () => i18n.t('convList:cond.op.notEmpty')),
]
const TIME_OPS = [
  opt('within', () => i18n.t('convList:cond.op.within')),
  opt('not_within', () => i18n.t('convList:cond.op.notWithin')),
  opt('after', () => i18n.t('convList:cond.op.after')),
  opt('before', () => i18n.t('convList:cond.op.before')),
  opt('between', () => i18n.t('convList:cond.op.between')),
]
const IS_OP = [opt('is', () => i18n.t('convList:cond.op.is'))]

/** Thứ tự = thứ tự trong ô chọn "Trường". */
export const FIELD_DEFS: Record<ConditionField, FieldDef> = {
  tag: {
    get label() { return i18n.t('convList:cond.field.tag') },
    kind: 'tags',
    ops: [
      opt('any', () => i18n.t('convList:cond.op.tagAny')),
      opt('all', () => i18n.t('convList:cond.op.tagAll')),
      opt('none', () => i18n.t('convList:cond.op.notContains')),
      opt('empty', () => i18n.t('convList:cond.op.tagEmpty')),
      opt('not_empty', () => i18n.t('convList:cond.op.tagNotEmpty')),
    ],
  },
  account: { get label() { return i18n.t('convList:cond.field.account') }, kind: 'accounts', ops: LIST_OPS },
  channel: { get label() { return i18n.t('convList:cond.field.channel') }, kind: 'channels', ops: LIST_OPS },
  email: { get label() { return i18n.t('convList:cond.field.email') }, kind: 'text', ops: TEXT_OPS },
  phone: { get label() { return i18n.t('convList:cond.field.phone') }, kind: 'text', ops: TEXT_OPS },
  assignee: {
    get label() { return i18n.t('convList:cond.field.assignee') },
    kind: 'members',
    ops: [...LIST_OPS, opt('empty', () => i18n.t('convList:cond.op.unassigned')), opt('not_empty', () => i18n.t('convList:cond.op.assigned'))],
  },
  lastMessageAt: { get label() { return i18n.t('convList:cond.field.lastMessageAt') }, kind: 'time', ops: TIME_OPS },
  lastCustomerMessageAt: { get label() { return i18n.t('convList:cond.field.lastCustomerMessageAt') }, kind: 'time', ops: TIME_OPS },
  createdAt: { get label() { return i18n.t('convList:cond.field.createdAt') }, kind: 'time', ops: TIME_OPS },
  threadType: {
    get label() { return i18n.t('convList:cond.field.threadType') },
    kind: 'single',
    ops: IS_OP,
    options: [
      opt('user', () => i18n.t('convList:cond.option.user')),
      opt('group', () => i18n.t('convList:cond.option.group')),
    ],
  },
  status: {
    get label() { return i18n.t('convList:cond.field.status') },
    kind: 'single',
    ops: IS_OP,
    options: [
      opt('unread', () => i18n.t('convList:cond.option.unread')),
      opt('read', () => i18n.t('convList:cond.option.read')),
      opt('unreplied', () => i18n.t('convList:cond.option.unreplied')),
      opt('replied', () => i18n.t('convList:cond.option.replied')),
    ],
  },
  aiMode: {
    get label() { return i18n.t('convList:cond.field.aiMode') },
    kind: 'multi',
    ops: LIST_OPS,
    options: [
      opt('manual', () => i18n.t('convList:cond.option.manual')),
      opt('auto', () => i18n.t('convList:cond.option.auto')),
      opt('suggest', () => i18n.t('convList:cond.option.suggest')),
    ],
  },
  lifecycle: {
    get label() { return i18n.t('convList:cond.field.lifecycle') },
    kind: 'multi',
    ops: LIST_OPS,
    options: [
      opt('subscriber', () => i18n.t('convList:cond.option.subscriber')),
      opt('lead', () => i18n.t('convList:cond.option.lead')),
      opt('qualified', () => i18n.t('convList:cond.option.qualified')),
      opt('opportunity', () => i18n.t('convList:cond.option.opportunity')),
      opt('customer', () => i18n.t('convList:cond.option.customer')),
      opt('evangelist', () => i18n.t('convList:cond.option.evangelist')),
      opt('churned', () => i18n.t('convList:cond.option.churned')),
    ],
  },
}

export const FIELD_ORDER = Object.keys(FIELD_DEFS) as ConditionField[]

/** Toán tử không cần giá trị. */
export const NO_VALUE_OPS = new Set(['empty', 'not_empty'])

export const LOGIC_LABEL: Record<ConditionLogic, string> = {
  get and() { return i18n.t('convList:cond.logic.and') },
  get or() { return i18n.t('convList:cond.logic.or') },
}

/** Điều kiện mới cho một trường: toán tử đầu tiên + giá trị mặc định hợp lý. */
export function newCondition(field: ConditionField = 'tag'): InboxCondition {
  const def = FIELD_DEFS[field]
  const op = def.ops[0].value
  return { field, op, value: defaultValue(field, op) }
}

export function defaultValue(field: ConditionField, op: string): unknown {
  if (NO_VALUE_OPS.has(op)) return undefined
  const kind = FIELD_DEFS[field].kind
  if (kind === 'time') {
    if (op === 'within' || op === 'not_within') return { amount: 1, unit: 'day' } satisfies RelativeValue
    if (op === 'between') return ['', '']
    return ''
  }
  if (kind === 'text' || kind === 'single') return ''
  return []
}

/** Đổi trường → về toán tử/giá trị mặc định của trường mới. */
export function changeField(c: InboxCondition, field: ConditionField): InboxCondition {
  return c.field === field ? c : newCondition(field)
}

/** Đổi toán tử → giữ giá trị nếu còn cùng dạng, không thì về mặc định. */
export function changeOp(c: InboxCondition, op: string): InboxCondition {
  const shape = (o: string) =>
    NO_VALUE_OPS.has(o) ? 'none' : o === 'within' || o === 'not_within' ? 'rel' : o === 'between' ? 'range' : 'plain'
  const keep = shape(op) === shape(c.op)
  return { field: c.field, op, value: keep ? c.value : defaultValue(c.field, op) }
}

const validDate = (v: unknown) => typeof v === 'string' && v !== '' && !Number.isNaN(new Date(v).getTime())

/** Điều kiện đã điền đủ để gửi lên chưa (chưa đủ thì bỏ qua khi lọc). */
export function isComplete(c: InboxCondition): boolean {
  const def = FIELD_DEFS[c.field]
  if (!def || !def.ops.some((o) => o.value === c.op)) return false
  if (NO_VALUE_OPS.has(c.op)) return true
  const v = c.value
  switch (def.kind) {
    case 'text':
    case 'single':
      return typeof v === 'string' && v.trim() !== ''
    case 'time':
      if (c.op === 'within' || c.op === 'not_within') {
        const r = v as RelativeValue | undefined
        return !!r && Number.isInteger(r.amount) && r.amount >= 1 && (r.unit === 'hour' || r.unit === 'day')
      }
      if (c.op === 'between') {
        return Array.isArray(v) && v.length === 2 && v.every(validDate) && new Date(v[0]) <= new Date(v[1])
      }
      return validDate(v)
    default:
      return Array.isArray(v) && v.length > 0
  }
}

/** Chuẩn hoá trước khi gửi: bỏ điều kiện dở dang, bỏ giá trị thừa, trim chữ. */
export function cleanConditions(list: InboxCondition[]): InboxCondition[] {
  return list.filter(isComplete).map((c) => {
    if (NO_VALUE_OPS.has(c.op)) return { field: c.field, op: c.op }
    if (typeof c.value === 'string') return { field: c.field, op: c.op, value: c.value.trim() }
    return { field: c.field, op: c.op, value: c.value }
  })
}

/** Tham số query cho GET /conversations (rỗng = không lọc nâng cao). */
export function conditionsQuery(list: InboxCondition[], logic: ConditionLogic): { conditions?: string; logic?: ConditionLogic } {
  const clean = cleanConditions(list)
  if (clean.length === 0) return {}
  return { conditions: JSON.stringify(clean), logic }
}

/** Hai bộ điều kiện có giống nhau không (để biết đang "sửa tạm" chế độ xem). */
export function sameConditions(
  a: InboxCondition[],
  aLogic: ConditionLogic,
  b: InboxCondition[],
  bLogic: ConditionLogic,
): boolean {
  const ca = cleanConditions(a)
  const cb = cleanConditions(b)
  if (ca.length !== cb.length) return false
  if (ca.length > 1 && aLogic !== bLogic) return false
  return JSON.stringify(ca) === JSON.stringify(cb)
}

/** Ngày giờ ISO → giá trị ô `datetime-local` (giờ máy người dùng). */
export function toLocalInput(iso: unknown): string {
  if (typeof iso !== 'string' || !iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Giá trị ô `datetime-local` → ISO (rỗng giữ rỗng). */
export function fromLocalInput(v: string): string {
  if (!v) return ''
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString()
}
